import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import config from "../../config";
import { setAuthData } from "../../slices/authSlice";
import "./signup.css";
import shmLogo from "../../assets/images/logo1.jpeg";
import conferenceImg from "../../assets/images/conferenceImg.png";
import backimage from "../../assets/images/back-image.jpg";

const SignUp = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [isRegistrationExpired, setIsRegistrationExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { eventId } = useSelector((state) => state.auth);

  // If user is already authenticated, redirect to profile.
  useEffect(() => {
    if (token) {
      navigate("/profile");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (eventId && config.API_LAST_DATE) {
      const url = config.API_LAST_DATE
        .replace("{EventID}", eventId)
        .replace("{typ}", "1");
      axios
        .post(url, {})
        .then((response) => {
          if (
            response.data &&
            response.data.Table &&
            response.data.Table.length > 0 &&
            response.data.Table[0].theDate
          ) {
            const apiDate = new Date(response.data.Table[0].theDate);
            apiDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (today > apiDate) {
              setIsRegistrationExpired(true);
            }
          }
        })
        .catch((error) =>
          console.error("Error fetching registration date:", error)
        )
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [eventId]);

  if (isLoading) {
    return (
      <div
        className="loading"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <h2>Loading...</h2>
      </div>
    );
  }

  if (isRegistrationExpired) {
    return (
      <div
        className="registration-expired"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <h1>Registration Date has Expired.</h1>
      </div>
    );
  }

  return (
    <div className="signup-container">
      <div className="signup-text">
        <div className="signup-text-container">
          <img src={conferenceImg} alt="Informatica Logo" className="img" />
        </div>
      </div>
      <div className="signup-form-panel">
        <div className="background-image-container">
          <img src={backimage} alt="Background" />
        </div>
        <div className="signup-wrapper">
          <div className="signup-form-section">
            <div className="signup-logo">
              <img src={shmLogo} alt="Logo Placeholder" />
            </div>
            <UserSignUp />
          </div>
        </div>
      </div>
    </div>
  );
};

const UserSignUp = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    username: "",
  });
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleCaptchaChange = (value) => {
    setCaptchaVerified(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const generateRandomDeviceId = () => {
    return (
      Math.random().toString(36).substring(2) +
      Date.now().toString(36)
    );
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    const { username, email, password } = formData;
    let validationErrors = {};
    if (!username) {
      validationErrors.username = "Username is required.";
    }
    if (!email) {
      validationErrors.email = "Email is required.";
    } else if (!validateEmail(email)) {
      validationErrors.email = "Enter a valid email.";
    }
    if (!password) {
      validationErrors.password = "Password is required.";
    }
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0 || !captchaVerified) {
      if (!captchaVerified) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          captcha: "Please verify the captcha.",
        }));
      }
      return;
    }
    const deviceId = generateRandomDeviceId();
    const url = config.API_REGISTER_URL
      .replace("{DeviceID}", deviceId)
      .replace("{UserID}", encodeURIComponent(email))
      .replace("{Fullname}", encodeURIComponent(username))
      .replace("{Password}", encodeURIComponent(password));
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      const result = data.Table[0];
      if (result.Status === "Success") {
        dispatch(setAuthData({ deviceId, userId: email }));
        alert("A verification PIN has been sent to your email.");
        navigate("/verify-email");
      } else if (result.Status === "Email already in use") {
        setErrors({ email: "Email already in use" });
      } else {
        setErrors({ general: "Please try again later." });
      }
    } catch (error) {
      console.error("Sign up error:", error);
      setErrors({ general: "An error occurred. Please try again later." });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNavigateToLogin = () => {
    navigate("/login");
  };

  return (
    <form className="signup-form" onSubmit={handleSignUp}>
      <h2>Sign Up</h2>
      {errors.general && <div className="error-message">{errors.general}</div>}
      <div className="form-group">
        <label htmlFor="username">Full Name</label>
        <input
          type="text"
          id="username"
          name="username"
          value={formData.username}
          placeholder="Enter your Full Name"
          onChange={handleInputChange}
        />
        {errors.username && (
          <div className="error-message">{errors.username}</div>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleInputChange}
        />
        {errors.email && <div className="error-message">{errors.email}</div>}
      </div>
      <div className="form-group">
        <label htmlFor="password">Password</label>
        <input
          type="password"
          id="password"
          name="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleInputChange}
        />
        {errors.password && (
          <div className="error-message">{errors.password}</div>
        )}
      </div>
      <ReCAPTCHA
        sitekey={config.RECAPTCHA_SITE_KEY}
        onChange={handleCaptchaChange}
        className="g-recaptcha"
      />
      {errors.captcha && <div className="error-message">{errors.captcha}</div>}
      <button type="submit" className="signup-button">
        Sign Up
      </button>
      <div className="extra-buttons">
        <button
          type="button"
          className="alt-button"
          onClick={handleNavigateToLogin}
        >
          Sign In
        </button>
      </div>
    </form>
  );
};

export default SignUp;
